import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Bot, Sparkles } from "lucide-react";
import ChatHint from "../components/ChatHint";
import EmptyStateCards from "../components/EmptyStateCards";

const STATUS = [
  { v: "aktiv", label: "Aktiv" },
  { v: "entwurf", label: "Entwurf" },
  { v: "archiviert", label: "Archiviert" },
];
const STATUS_COLOR = {
  aktiv: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  entwurf: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  archiviert: "bg-slate-500/15 text-slate-400 border-slate-500/20",
};
const EUR = (v, c = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency: c || "EUR" }).format(v || 0);

const emptyForm = {
  name: "", description: "", price: 0, currency: "EUR", category: "",
  sku: "", stock: 0, status: "aktiv", image_url: "", tags: "",
};

export default function Produkte() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
      tags: typeof form.tags === "string"
        ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : form.tags,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produkt aktualisiert");
      } else {
        await api.post("/products", payload);
        toast.success("Produkt erstellt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    }
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${confirmDel.id}`);
      toast.success("Produkt gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="produkte-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Katalog</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Produkte</h1>
          <p className="text-sm text-slate-400 mt-2">{items.length === 0 ? "Noch leer." : `${items.length} Produkte.`}</p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Lege ein Produkt an.“'} /></div>
        </div>
        <Button onClick={openCreate} data-testid="add-product-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neues Produkt
        </Button>
      </header>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Lade…</div>
          ) : items.length === 0 ? (
            <EmptyStateCards
              testid="produkte-empty"
              title="Noch keine Produkte"
              subtitle="Lege dein erstes Produkt an — in 10 Sekunden mit nur Name und Preis."
              cards={[
                { icon: Plus, title: "Produkt anlegen", body: "Name + Preis reichen schon. Alles andere später.", onClick: openCreate, testid: "empty-card-create" },
                { icon: Bot, title: "KI-Agent fragen", body: '„Lege ein Produkt T-Shirt für 29€ an“ — fertig.', to: "/ki-agent", testid: "empty-card-chat" },
                { icon: Sparkles, title: "Bild erstellen", body: "Produktfoto per KI generieren und direkt verknüpfen.", to: "/bild-studio", testid: "empty-card-image" },
              ]}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Produkt</TableHead>
                  <TableHead className="text-slate-400">Kategorie</TableHead>
                  <TableHead className="text-slate-400">SKU</TableHead>
                  <TableHead className="text-slate-400 text-right">Preis</TableHead>
                  <TableHead className="text-slate-400 text-right">Lager</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`product-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="h-10 w-10 rounded-md object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded-md bg-white/5 grid place-items-center">
                            <Package className="h-4 w-4 text-slate-500" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-white">{p.name}</div>
                          <div className="text-xs text-slate-500 truncate max-w-xs">{p.description}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">{p.category || "—"}</TableCell>
                    <TableCell className="text-slate-500 font-mono text-xs">{p.sku || "—"}</TableCell>
                    <TableCell className="text-right text-white tabular-nums">{EUR(p.price, p.currency)}</TableCell>
                    <TableCell className="text-right text-slate-300 tabular-nums">{p.stock}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLOR[p.status]} border font-normal`} data-testid={`product-status-${p.id}`}>
                        {STATUS.find((s) => s.v === p.status)?.label || p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)}
                        data-testid={`product-edit-${p.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(p)}
                        data-testid={`product-delete-${p.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* Essentials — above the fold */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" required className="md:col-span-2">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-form-name" placeholder="z. B. Sommer-T-Shirt" />
              </Field>
              <Field label="Preis (EUR)">
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" />
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="product-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Beschreibung" className="md:col-span-2">
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="product-form-description" placeholder="Kurzbeschreibung — eine Zeile reicht." />
              </Field>
            </div>

            {/* Erweitert — collapsed, sensible defaults */}
            <details className="group border-t border-white/5 pt-3">
              <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="product-form-advanced-toggle">
                <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (SKU, Lager, Bild, Tags)
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <Field label="SKU">
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-form-sku" />
                </Field>
                <Field label="Lagerbestand">
                  <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-form-stock" />
                </Field>
                <Field label="Kategorie" className="md:col-span-2">
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category" />
                </Field>
                <Field label="Bild-URL" className="md:col-span-2">
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="product-form-image" />
                </Field>
                <Field label="Tags (kommagetrennt)" className="md:col-span-2">
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="product-form-tags" />
                </Field>
              </div>
            </details>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="product-form-cancel">Abbrechen</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="product-form-submit">
                {editing ? "Änderungen speichern" : "Produkt anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              „{confirmDel?.name}" wird dauerhaft entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="product-delete-confirm" className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-300 text-xs mb-1 block">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="p-16 text-center" data-testid="produkte-empty">
      <Package className="h-10 w-10 text-slate-600 mx-auto mb-3" />
      <div className="text-white font-semibold mb-1">Noch keine Produkte</div>
      <div className="text-sm text-slate-400 mb-5">Lege dein erstes Produkt an, um loszulegen.</div>
      <Button onClick={onCreate} className="bg-emerald-500 hover:bg-emerald-600">
        <Plus className="h-4 w-4 mr-2" /> Neues Produkt
      </Button>
    </div>
  );
}
